import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  FileText,
  Sparkles,
  Zap,
  ExternalLink,
  ArrowUpDown,
  Search,
  X,
  BookOpen,
} from "lucide-react";
import { usePagination } from "@/hooks/usePagination";
import type { KnowledgeBaseBook, KnowledgeBaseFilters } from "@/api/aiKnowledgeBase";
import type { Category, Language } from "@/types";

interface BookContextTableProps {
  books: KnowledgeBaseBook[];
  totalCount: number;
  page: number;
  pageSize: number;
  filters: KnowledgeBaseFilters;
  sortField: string;
  sortAsc: boolean;
  selectedIds: Set<number>;
  categories: Category[];
  languages: Language[];
  isLoading: boolean;
  isOperationRunning: boolean;
  onPageChange: (page: number) => void;
  onFiltersChange: (filters: KnowledgeBaseFilters) => void;
  onSortChange: (field: string) => void;
  onSelectionChange: (ids: Set<number>) => void;
  onPrefill: (bookId: number) => void;
  onGenerate: (bookId: number) => void;
  onPrefillAndGenerate: (bookId: number) => void;
}

function formatRelativeTime(dateStr: string) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
}

const BookContextTable = ({
  books,
  totalCount,
  page,
  pageSize,
  filters,
  sortField,
  sortAsc,
  selectedIds,
  categories,
  languages,
  isLoading,
  isOperationRunning,
  onPageChange,
  onFiltersChange,
  onSortChange,
  onSelectionChange,
  onPrefill,
  onGenerate,
  onPrefillAndGenerate,
}: BookContextTableProps) => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState(filters.search);
  const { totalPages, canGoPrev, canGoNext, pageNumbers } = usePagination({
    totalItems: totalCount,
    itemsPerPage: pageSize,
    currentPage: page,
  });

  const allOnPageSelected = books.length > 0 && books.every((b) => selectedIds.has(b.id));

  const handleSelectAll = () => {
    if (allOnPageSelected) {
      const newIds = new Set(selectedIds);
      books.forEach((b) => newIds.delete(b.id));
      onSelectionChange(newIds);
    } else {
      const newIds = new Set(selectedIds);
      books.forEach((b) => newIds.add(b.id));
      onSelectionChange(newIds);
    }
  };

  const handleSelectRow = (id: number) => {
    const newIds = new Set(selectedIds);
    if (newIds.has(id)) {
      newIds.delete(id);
    } else {
      newIds.add(id);
    }
    onSelectionChange(newIds);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      onFiltersChange({ ...filters, search: searchInput });
    }
  };

  const handleSearchBlur = () => {
    if (searchInput !== filters.search) {
      onFiltersChange({ ...filters, search: searchInput });
    }
  };

  const handleResetFilters = () => {
    setSearchInput("");
    onFiltersChange({
      search: "",
      aiContextStatus: "all",
      embeddingStatus: "all",
      language: "",
      category: "",
    });
  };

  const hasActiveFilters =
    filters.search ||
    filters.aiContextStatus !== "all" ||
    filters.embeddingStatus !== "all" ||
    filters.language ||
    filters.category;

  const SortableHeader = ({ field, children }: { field: string; children: React.ReactNode }) => (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8"
      onClick={() => onSortChange(field)}
    >
      {children}
      <ArrowUpDown className="ml-1 h-3 w-3" />
    </Button>
  );

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search books..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            onBlur={handleSearchBlur}
            className="pl-8"
          />
        </div>

        <Select
          value={filters.aiContextStatus}
          onValueChange={(val) =>
            onFiltersChange({ ...filters, aiContextStatus: val as any })
          }
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="AI Context" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Context</SelectItem>
            <SelectItem value="has">Has Context</SelectItem>
            <SelectItem value="missing">Missing Context</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={filters.embeddingStatus}
          onValueChange={(val) =>
            onFiltersChange({ ...filters, embeddingStatus: val as any })
          }
        >
          <SelectTrigger className="w-[170px]">
            <SelectValue placeholder="Embeddings" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Embeddings</SelectItem>
            <SelectItem value="has">Has Embeddings</SelectItem>
            <SelectItem value="missing">Missing Embeddings</SelectItem>
          </SelectContent>
        </Select>

        {languages.length > 0 && (
          <Select
            value={filters.language || "all"}
            onValueChange={(val) =>
              onFiltersChange({ ...filters, language: val === "all" ? "" : val })
            }
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Language" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Languages</SelectItem>
              {languages.map((lang) => (
                <SelectItem key={lang.code} value={lang.code}>
                  {lang.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {categories.length > 0 && (
          <Select
            value={filters.category || "all"}
            onValueChange={(val) =>
              onFiltersChange({ ...filters, category: val === "all" ? "" : val })
            }
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.name}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={handleResetFilters}>
            <X className="h-4 w-4 mr-1" />
            Reset
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]">
                <Checkbox
                  checked={allOnPageSelected}
                  onCheckedChange={handleSelectAll}
                  disabled={isOperationRunning}
                />
              </TableHead>
              <TableHead>
                <SortableHeader field="title">Title</SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader field="summaryContents">Editions</SortableHeader>
              </TableHead>
              <TableHead>AI Context</TableHead>
              <TableHead>Embeddings</TableHead>
              <TableHead>
                <SortableHeader field="totalEmbeddings">Chunks</SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader field="updatedAt">Updated</SortableHeader>
              </TableHead>
              <TableHead className="w-[60px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 8 }).map((_, j) => (
                    <TableCell key={j}>
                      <div className="h-4 bg-muted animate-pulse rounded" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : books.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  No books found matching your filters.
                </TableCell>
              </TableRow>
            ) : (
              books.map((book) => (
                <TableRow key={book.id} className={selectedIds.has(book.id) ? "bg-muted/50" : ""}>
                  <TableCell>
                    <Checkbox
                      checked={selectedIds.has(book.id)}
                      onCheckedChange={() => handleSelectRow(book.id)}
                      disabled={isOperationRunning}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {book.coverImage && (
                        <img
                          src={book.coverImage}
                          alt=""
                          className="h-10 w-7 rounded object-cover flex-shrink-0"
                        />
                      )}
                      <div className="min-w-0">
                        <div className="font-medium truncate max-w-[220px]">{book.title}</div>
                        {!book.isActive && (
                          <Badge variant="outline" className="text-xs mt-0.5">
                            Inactive
                          </Badge>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {book.summaryContents.length > 0 ? (
                        book.summaryContents.map((sc) => {
                          const lang = languages?.find((l) => l.code === sc.language);
                          const languageName = lang?.name || sc.language;
                          const contentTypeName = sc.contentType === 'full_book' ? 'Full Book' : 'Summary';

                          return (
                            <Badge
                              key={`${sc.contentType}-${sc.language}`}
                              className="bg-secondary text-secondary-foreground flex items-center"
                              title={`${contentTypeName} - ${languageName}${
                                sc.chapterCount ? ` - ${sc.chapterCount} chapters` : ""
                              }`}
                            >
                              {sc.contentType === 'full_book' ? (
                                <BookOpen className="h-3 w-3 mr-1 text-violet-500" />
                              ) : (
                                <Zap className="h-3 w-3 mr-1 text-amber-500" />
                              )}
                              {sc.language.toUpperCase()}
                              {sc.chapterCount > 0 && (
                                <div className="ml-1.5 flex items-center gap-0.5 bg-primary/10 px-1 rounded-sm text-primary">
                                  <BookOpen className="h-2.5 w-2.5" />
                                  <span className="text-[10px] font-medium">
                                    {sc.chapterCount}
                                  </span>
                                </div>
                              )}
                            </Badge>
                          );
                        })
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          No content
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {book.summaryContents.map((sc) => {
                        const statusColors = {
                          up_to_date: "bg-green-100 text-green-800 hover:bg-green-100",
                          outdated: "bg-yellow-100 text-yellow-800 hover:bg-yellow-100",
                          missing: "",
                        };
                        if (!sc.hasAiContext) {
                          return (
                            <Badge key={sc.language} variant="secondary" className="text-xs">
                              {sc.language.toUpperCase()}: Missing
                            </Badge>
                          );
                        }
                        return (
                          <Badge key={sc.language} className={`text-xs ${statusColors[sc.aiContextStatus]}`}>
                            {sc.language.toUpperCase()}: {sc.wordCount.toLocaleString()}w
                            {sc.aiContextStatus === "outdated" && " \u2022 Outdated"}
                          </Badge>
                        );
                      })}
                      {book.summaryContents.length === 0 && (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {book.summaryContents.map((sc) => {
                        const embCount = book.embeddingsByLanguage[sc.language] || 0;
                        if (embCount === 0) {
                          return (
                            <Badge key={sc.language} variant="secondary" className="text-xs">
                              {sc.language.toUpperCase()}: None
                            </Badge>
                          );
                        }
                        const statusColors = {
                          up_to_date: "bg-green-100 text-green-800 hover:bg-green-100",
                          outdated: "bg-yellow-100 text-yellow-800 hover:bg-yellow-100",
                          missing: "",
                        };
                        return (
                          <Badge key={sc.language} className={`text-xs ${statusColors[sc.embeddingsStatus]}`}>
                            {sc.language.toUpperCase()}: {embCount}
                            {sc.embeddingsStatus === "outdated" && " \u2022 Outdated"}
                          </Badge>
                        );
                      })}
                      {book.summaryContents.length === 0 && (
                        <Badge variant="secondary" className="text-xs">None</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{book.totalEmbeddings}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatRelativeTime(book.updatedAt)}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" disabled={isOperationRunning}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onPrefill(book.id)}>
                          <FileText className="h-4 w-4 mr-2" />
                          Pre-fill from Chapters
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onGenerate(book.id)}>
                          <Sparkles className="h-4 w-4 mr-2" />
                          Generate Embeddings
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onPrefillAndGenerate(book.id)}>
                          <Zap className="h-4 w-4 mr-2" />
                          Pre-fill + Generate
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/admin/books/${book.id}/edit`)}>
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Edit in BookForm
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalCount > pageSize && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} of{" "}
            {totalCount} books
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={!canGoPrev}
              onClick={() => onPageChange(page - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {pageNumbers.map((p) => (
              <Button
                key={p}
                variant={p === page ? "default" : "outline"}
                size="icon"
                className="h-8 w-8"
                onClick={() => onPageChange(p)}
              >
                {p}
              </Button>
            ))}
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={!canGoNext}
              onClick={() => onPageChange(page + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookContextTable;
